import { z } from 'zod';
import { ClientCommandIdSchema } from './client-command-id';
import { DeckIdSchema, SpaceIdSchema, SlideIdSchema } from './entity-ids';
import { DeckFormatSchema } from './deck-format';
import { SlideStatusSchema } from './slide-status';
import { SlideFactsSchema } from './fact-types';
import { ChartDataSourceSchema } from './chart-types';

/**
 * Data-tab links for the charts a duplicate creates, keyed by the SOURCE
 * chart's data-id. Filled server-side before the command runs: every copy of
 * a linked chart gets its own copy of the data tab.
 */
const DuplicatedChartDataSourcesSchema = z
  .record(z.string(), ChartDataSourceSchema)
  .optional();

export const DeckOperationSourceSchema = z.enum(['manual', 'ai', 'system']);

export const DeckHistoryActionSchema = z.enum(['apply', 'undo', 'redo']);
export type DeckHistoryAction = z.infer<typeof DeckHistoryActionSchema>;

export const DeckCommandKindSchema = z.enum([
  'deck.patch',
  'slide.create',
  'slide.duplicate',
  'slide.delete',
  'slide.reorder',
  'slide.patch',
  'slide.freeze',
  'slide.setFacts',
  'element.create',
  'element.patch',
  'element.batchPatch',
  'element.delete',
  'element.duplicate',
  'element.reorder',
  'element.lock',
  'table.patch',
  'chart.patch',
]);

export const SlideSnapshotSchema = z.object({
  id: SlideIdSchema,
  deckId: DeckIdSchema,
  order: z.number().int().nonnegative(),
  html: z.string(),
  notes: z.string().nullable().optional(),
  status: SlideStatusSchema.optional(),
  facts: SlideFactsSchema.nullable().optional(),
  /** @deprecated Legacy stored payloads only; normalize via normalizeSlideStatus. */
  isDraft: z.boolean().optional(),
});
export type SlideSnapshot = z.infer<typeof SlideSnapshotSchema>;

export const DeckPatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  name: z.string().min(1).optional(),
  outline: z.string().nullable().optional(),
});

export const SlideCreatePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  insertAt: z.number().int().nonnegative().optional(),
  html: z.string().optional(),
  notes: z.string().nullable().optional(),
  status: SlideStatusSchema.optional(),
  /** @deprecated Legacy stored payloads only; normalize via normalizeSlideStatus. */
  isDraft: z.boolean().optional(),
  slide: SlideSnapshotSchema.optional(),
});

export const SlideDuplicatePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  duplicatedSlideId: SlideIdSchema,
  insertAt: z.number().int().nonnegative().optional(),
  duplicatedSlide: SlideSnapshotSchema.optional(),
  chartDataSources: DuplicatedChartDataSourcesSchema,
});

export const SlideDeletePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  deletedSlide: SlideSnapshotSchema.optional(),
});

export const SlideReorderPayloadSchema = z.object({
  deckId: DeckIdSchema,
  orderedSlideIds: z.array(SlideIdSchema).min(1),
});

export const SlidePatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  html: z.string().optional(),
  notes: z.string().nullable().optional(),
  status: SlideStatusSchema.optional(),
  /**
   * Shadow-freeze output to cache alongside a FINAL publish (the sync engine
   * runs credential-less, so the cache rides the command). The handler strips
   * it from the stored operation payloads.
   */
  frozenHtml: z.string().nullable().optional(),
  /** @deprecated Legacy stored payloads only; normalize via normalizeSlideStatus. */
  isDraft: z.boolean().optional(),
});

// Enables freeform editing: swaps the FINAL slide's raw document for its
// compiled frozen fragment and flips status to FROZEN. Dispatched with
// source 'system' so it never enters undo lanes (the flip is permanent).
// `html`/`sourceHash` are filled server-side before the transaction (cache
// swap or inline freeze); stored forward payloads carry the frozen html so
// version-state replay never needs a browser.
export const SlideFreezePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  html: z.string().optional(),
  sourceHash: z.string().optional(),
  /** Inverse payloads only: the pre-freeze status (and html above), so
   * history reconstruction can restore the authored document. Forward
   * applies never set it. */
  status: SlideStatusSchema.optional(),
});

// Whole-value replace of a slide's recorded facts (sources). Self-inverting:
// the inverse carries the previous value. Facts are provenance metadata, not
// slide HTML, so the command applies on any slide status.
export const SlideSetFactsPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  facts: SlideFactsSchema.nullable(),
});

const NullableStyleValueSchema = z.union([z.string(), z.null()]);
const StylePatchSchema = z.record(z.string(), NullableStyleValueSchema);
const AttributePatchSchema = z.record(z.string(), NullableStyleValueSchema);

export const ElementPatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementId: z.string(),
  stylePatch: StylePatchSchema.optional(),
  attributePatch: AttributePatchSchema.optional(),
  innerHtml: z.string().optional(),
});

export const ElementBatchPatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  patches: z.array(
    z.object({
      elementId: z.string(),
      stylePatch: StylePatchSchema.optional(),
      attributePatch: AttributePatchSchema.optional(),
      innerHtml: z.string().optional(),
    })
  ),
});

export const ElementCreatePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementHtml: z.string(),
  insertAfterElementId: z.string().nullable().optional(),
});

export const ElementDeletePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementId: z.string(),
  deletedElementHtml: z.string().optional(),
  insertAfterElementId: z.string().nullable().optional(),
});

export const ElementDuplicatePayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementId: z.string(),
  duplicatedElementId: z.string().optional(),
  duplicatedElementHtml: z.string().optional(),
  chartDataSources: DuplicatedChartDataSourcesSchema,
});

// `zIndex: null` / `locked: null` mean "remove the property/attribute".
// Undo inverses use null to restore elements that had no explicit value.
export const ElementReorderPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementId: z.string(),
  zIndex: z.number().int().nullable(),
});

export const ElementLockPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  elementId: z.string(),
  locked: z.boolean().nullable(),
});

export const TablePatchOperationSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('insertRow'),
    index: z.number().int(),
    cells: z.array(z.string()).optional(),
  }),
  z.object({
    type: z.literal('deleteRow'),
    index: z.number().int(),
  }),
  z.object({
    type: z.literal('insertColumn'),
    index: z.number().int(),
    cells: z.array(z.string()).optional(),
  }),
  z.object({
    type: z.literal('deleteColumn'),
    index: z.number().int(),
  }),
  z.object({
    type: z.literal('setCellHtml'),
    cellId: z.string().optional(),
    rowIndex: z.number().int().nonnegative().optional(),
    columnIndex: z.number().int().nonnegative().optional(),
    html: z.string(),
  }),
  z.object({
    type: z.literal('setCellStyle'),
    cellId: z.string().optional(),
    rowIndex: z.number().int().nonnegative().optional(),
    columnIndex: z.number().int().nonnegative().optional(),
    stylePatch: StylePatchSchema,
  }),
  z.object({
    type: z.literal('resizeRow'),
    rowIndex: z.number().int().nonnegative(),
    height: z.string(),
  }),
  z.object({
    type: z.literal('resizeColumn'),
    columnIndex: z.number().int().nonnegative(),
    width: z.string(),
  }),
  z.object({
    type: z.literal('replaceHtml'),
    html: z.string(),
  }),
]);
export type TablePatchOperation = z.infer<typeof TablePatchOperationSchema>;

export const TablePatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  tableId: z.string(),
  operation: TablePatchOperationSchema,
});

export const ChartPatchPayloadSchema = z.object({
  deckId: DeckIdSchema,
  slideId: SlideIdSchema,
  chartId: z.string(),
  chartSpec: z.unknown(),
});

export const DeckCommandSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('deck.patch'),
    payload: DeckPatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.create'),
    payload: SlideCreatePayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.duplicate'),
    payload: SlideDuplicatePayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.delete'),
    payload: SlideDeletePayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.reorder'),
    payload: SlideReorderPayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.patch'),
    payload: SlidePatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.freeze'),
    payload: SlideFreezePayloadSchema,
  }),
  z.object({
    kind: z.literal('slide.setFacts'),
    payload: SlideSetFactsPayloadSchema,
  }),
  z.object({
    kind: z.literal('element.create'),
    payload: ElementCreatePayloadSchema,
  }),
  z.object({
    kind: z.literal('element.patch'),
    payload: ElementPatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('element.batchPatch'),
    payload: ElementBatchPatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('element.delete'),
    payload: ElementDeletePayloadSchema,
  }),
  z.object({
    kind: z.literal('element.duplicate'),
    payload: ElementDuplicatePayloadSchema,
  }),
  z.object({
    kind: z.literal('element.reorder'),
    payload: ElementReorderPayloadSchema,
  }),
  z.object({
    kind: z.literal('element.lock'),
    payload: ElementLockPayloadSchema,
  }),
  z.object({
    kind: z.literal('table.patch'),
    payload: TablePatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('chart.patch'),
    payload: ChartPatchPayloadSchema,
  }),
]);
export type DeckCommand = z.infer<typeof DeckCommandSchema>;

export const DeckCommandEnvelopeSchema = z.object({
  clientCommandId: ClientCommandIdSchema,
  clientId: z.string().min(1),
  actorSessionId: z.string().min(1).optional(),
  actorRunId: z.string().min(1).optional(),
  source: DeckOperationSourceSchema.default('manual'),
  groupId: z.string().min(1).optional(),
  baseSequence: z.number().int().nonnegative(),
  command: DeckCommandSchema,
});
export type DeckCommandEnvelope = z.infer<typeof DeckCommandEnvelopeSchema>;

export const DeckOperationSchema = z.object({
  id: z.string(),
  deckId: DeckIdSchema,
  sequence: z.number().int().nonnegative(),
  actorUserId: z.string().nullable().optional(),
  actorRunId: z.string().nullable().optional(),
  actorSessionId: z.string().nullable().optional(),
  clientId: z.string().nullable().optional(),
  clientCommandId: z.string().nullable().optional(),
  source: DeckOperationSourceSchema,
  groupId: z.string().nullable().optional(),
  kind: DeckCommandKindSchema,
  historyAction: DeckHistoryActionSchema,
  targetOperationId: z.string().nullable().optional(),
  baseSequence: z.number().int().nonnegative(),
  payload: z.unknown(),
  inversePayload: z.unknown(),
  affectedSlideIds: z.array(SlideIdSchema),
  createdAt: z.string(),
});
export type DeckOperation = z.infer<typeof DeckOperationSchema>;

// Wire shape returned by /commands, /history/undo, and /history/redo.
export const DeckCommandResultSlideSchema = z.object({
  id: SlideIdSchema,
  deckId: DeckIdSchema,
  order: z.number().int().nonnegative(),
  html: z.string(),
  notes: z.string().nullable(),
  // Optional so a new client tolerates a pre-status backend during deploy
  // skew; consumers normalize via normalizeSlideStatus.
  status: SlideStatusSchema.optional(),
  facts: SlideFactsSchema.nullable(),
  /** @deprecated Wire-compat emission for stale clients; logic keys on status. */
  isDraft: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DeckCommandResultSlide = z.infer<
  typeof DeckCommandResultSlideSchema
>;

export const DeckCommandResultDeckSchema = z.object({
  id: DeckIdSchema,
  name: z.string(),
  organizationId: z.string(),
  spaceId: SpaceIdSchema,
  spaceName: z.string().nullable(),
  outline: z.string().nullable(),
  format: DeckFormatSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  slideCount: z.number().int().nonnegative(),
  headSequence: z.number().int().nonnegative(),
});
export type DeckCommandResultDeck = z.infer<typeof DeckCommandResultDeckSchema>;

export const DeckCommandResultSchema = z.object({
  headSequence: z.number().int().nonnegative(),
  /** The last (primary) operation; `operations` lists every member of a
   * grouped undo/redo in execution order. */
  operation: DeckOperationSchema,
  operations: z.array(DeckOperationSchema),
  changedSlides: z.array(DeckCommandResultSlideSchema),
  deletedSlideIds: z.array(SlideIdSchema),
  deck: DeckCommandResultDeckSchema.optional(),
});
export type DeckCommandResult = z.infer<typeof DeckCommandResultSchema>;

export const DeckHistoryCursorSchema = z.object({
  undoAvailable: z.boolean(),
  redoAvailable: z.boolean(),
});
export type DeckHistoryCursor = z.infer<typeof DeckHistoryCursorSchema>;

export const DeckUndoRedoRequestSchema = z.object({
  clientCommandId: ClientCommandIdSchema,
  clientId: z.string().min(1),
  actorSessionId: z.string().min(1).optional(),
  baseSequence: z.number().int().nonnegative(),
  operationId: z.string().min(1).optional(),
});
export type DeckUndoRedoRequest = z.infer<typeof DeckUndoRedoRequestSchema>;
